import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

export const couponSchema = z.object({
  code: z
    .string()
    .min(1, "Coupon code is required")
    .max(20, "Coupon code must be less than 20 characters"),
});

export type CouponFormData = z.infer<typeof couponSchema>;

interface CouponFormProps {
  onSubmit: (data: CouponFormData) => Promise<void>;
  isPending: boolean;
}

export function CouponForm({ onSubmit, isPending }: CouponFormProps) {
  const form = useForm<CouponFormData>({
    resolver: zodResolver(couponSchema),
    defaultValues: {
      code: "",
    },
  });

  const handleSubmit = async (data: CouponFormData) => {
    try {
      await onSubmit(data);
      form.reset();
    } catch {
      form.setError("code", {
        type: "manual",
        message: "Invalid coupon code",
      });
    }
  };

  const handleButtonClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const isValid = await form.trigger();
    if (isValid) {
      const data = form.getValues();
      await handleSubmit(data);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };

  return (
    <form onSubmit={handleFormSubmit} className="mb-3">
      <div className="mb-3">
        <label htmlFor="code" className="form-label fw-semibold text-dark mb-2">
          Coupon Code
        </label>
        <div className="d-flex gap-3 align-items-start">
          <div className="flex-grow-1">
            <input
              {...form.register("code")}
              type="text"
              className={`form-control ${
                form.formState.errors.code ? "is-invalid" : ""
              }`}
              placeholder="Enter coupon code"
              style={{
                borderRadius: "12px",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                padding: "12px 16px",
                fontSize: "14px",
                transition: "all 0.2s ease",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
              }}
            />
            {form.formState.errors.code && (
              <div className="invalid-feedback d-block mt-1">
                {form.formState.errors.code.message}
              </div>
            )}
          </div>
          <button
            type="button"
            disabled={isPending}
            className="btn px-4"
            onClick={handleButtonClick}
            style={{
              height: "48px",
              borderRadius: "12px",
              border: "none",
              background: "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
              color: "white",
              fontWeight: "600",
              fontSize: "14px",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 15px rgba(74, 222, 128, 0.3)",
            }}
            onMouseEnter={(e) => {
              if (!isPending) {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(74, 222, 128, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 15px rgba(74, 222, 128, 0.3)";
            }}
          >
            {isPending ? (
              <div className="d-flex align-items-center">
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  style={{ width: "16px", height: "16px" }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span>Applying...</span>
              </div>
            ) : (
              <span>Apply</span>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}
